import { FC, useEffect, useMemo, useRef } from 'react';
import { BlockNoteViewRaw, useCreateBlockNote } from '@blocknote/react';
import { en } from '@blocknote/core/locales';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import { blue } from '@mui/material/colors';
import '@blocknote/react/style.css';

import stripTrailingEmptyParagraphs from './helpers/strip-trailing-empty-paragraphs';
import { MUIFormattingToolbar } from './components/MUIFormattingToolbar';
import { schema, TextBlock, BlockNoteDocument } from './schema';
import './editor.css';

const LINE_HEIGHT = 25;
const DEFAULT_LINES = 5;

const EditorContainer = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'extraLines' && prop !== 'editable',
})<{ extraLines: number; editable: boolean }>(
  ({ theme, extraLines, editable }) => ({
    border: editable ? `1px solid ${theme.palette.divider}` : 'none',
    borderRadius: theme.shape.borderRadius,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    cursor: 'text',
    padding: editable
      ? theme.spacing(
          theme.spacingSizes.xs,
          theme.spacingSizes.sm,
          theme.spacingSizes.sm,
          theme.spacingSizes.sm,
        )
      : 0,
    '.bn-editor': {
      minHeight: editable
        ? extraLines > 0
          ? (extraLines + DEFAULT_LINES) * LINE_HEIGHT
          : DEFAULT_LINES * LINE_HEIGHT
        : 'auto',
      cursor: editable ? 'text' : 'default',
    },
    '.bn-editor a': {
      pointerEvents: editable ? 'none' : 'auto',
      cursor: editable ? 'text' : 'pointer',
      color: blue[500],
    },
  }),
);

type Props = {
  value?: BlockNoteDocument;
  onChange?: (document: Array<TextBlock>) => void;
  placeholder?: string;
  extraLines?: number;
  readOnly?: boolean;
};

const BlockNoteEditor: FC<Props> = ({
  value,
  onChange,
  placeholder,
  extraLines = 0,
  readOnly = false,
}) => {
  const editable = !readOnly;

  const parsedValue = useMemo<BlockNoteDocument | undefined>(() => {
    try {
      return Array.isArray(value) && value.length > 0 ? value : undefined;
    } catch {
      return undefined;
    }
  }, [value]);

  const editor = useCreateBlockNote({
    schema,
    dictionary: {
      ...en,
      placeholders: {
        ...en.placeholders,
        default: '',
        emptyDocument: placeholder ?? '',
      },
    },
    initialContent: parsedValue,
  });

  // Track the last value string we pushed into the editor to avoid echo-back loops.
  const editorValueRef = useRef<string>(JSON.stringify(parsedValue ?? null));

  // Keep a stable ref to onChange so the unmount cleanup always calls the latest version.
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  const prevEditableRef = useRef(editable);

  // Sync externally-driven value changes into the editor.
  // Also force-syncs on editable→readonly transition so trailing empty paragraphs
  // stripped by onChange are reflected in the visual state before the editor goes readonly.
  useEffect(() => {
    const transitioningToReadonly = prevEditableRef.current && !editable;
    prevEditableRef.current = editable;

    const newStr = JSON.stringify(parsedValue ?? null);
    const hasExternalChange = newStr !== editorValueRef.current;

    if (hasExternalChange || transitioningToReadonly) {
      editorValueRef.current = newStr;
      if (parsedValue && parsedValue.length > 0) {
        editor.replaceBlocks(editor.document, parsedValue);
      }
    }
  }, [editable, parsedValue, editor]);

  // On unmount, deliver a clean document (trailing empty paragraph stripped) to the parent.
  useEffect(() => {
    return () => {
      const cleaned = stripTrailingEmptyParagraphs([...editor.document]);
      onChangeRef.current?.(cleaned);
    };
  }, [editor]);

  return (
    <EditorContainer extraLines={extraLines} editable={editable}>
      <BlockNoteViewRaw
        editor={editor}
        formattingToolbar={false}
        slashMenu={false}
        sideMenu={false}
        linkToolbar={false}
        filePanel={false}
        tableHandles={false}
        emojiPicker={false}
        onChange={({ document }) => {
          const cleaned = stripTrailingEmptyParagraphs([...document]);
          editorValueRef.current = JSON.stringify(cleaned);
          onChange?.(cleaned);
        }}
        theme="light"
        style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}
        editable={editable}
      >
        {editable ? <MUIFormattingToolbar /> : null}
      </BlockNoteViewRaw>
    </EditorContainer>
  );
};

export default BlockNoteEditor;
